import { Component, Host, Element, h, Prop, Watch, State, Event, EventEmitter } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import pfx from "../../utils/style-class-prefix-helper";
import { ProgressCircleSchemeDefinition, ProgressCircleSchemeName } from "./constants/progress-circle-types";
import { PROGRESS_CIRCLE_SCHEME_CONFIG, PROGRESS_CIRCLE_SCHEME_MAP } from "./constants/progress-cirlce-constants";
import { ProgressCircleSchemeConfiguration } from "../../constants/colors";
import { setCssVariable } from "../../utils/css-variables";
// import { InitLogging } from "../../decorators/init-logging";
@Component({
  tag: "eon-ui-progress-circle",
  styleUrl: "progress-circle.scss",
  shadow: true
})
export class ProgressCircle
  implements ColorableComponent<ProgressCircleSchemeConfiguration<ProgressCircleSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: ProgressCircleSchemeConfiguration<ProgressCircleSchemeDefinition> = PROGRESS_CIRCLE_SCHEME_CONFIG;
  schemeMap = PROGRESS_CIRCLE_SCHEME_MAP;
  // private _iconColor: string;

  @Element() hostElement: HTMLEonUiProgressCircleElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: ProgressCircleSchemeName;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: ProgressCircleSchemeDefinition;

  /**
   * The progress the circle should show in percent
   */
  @Prop() progress: number;
  /**
   * The icon which should be shown in the middle (only works when no text is provided)
   */
  @Prop() iconName: string;
  /**
   * Sets the progress circular in error mode
   */
  @Prop() error: boolean;
  /**
   * Shows the current progress as text in the center
   */
  @Prop() showProgressText: boolean;

  /**
   * The icon color as inline style (can be HEX or RGB Code)
   */
  @Prop() iconColor: string;

  /**
   * Controls whether the text should be animated counting up or not
   */
  @Prop() animateText: boolean = true;

  @State() progressText: string = "";

  /**
   * Fires when a user clicks on the icon inside the circle
   */
  @Event() iconClick: EventEmitter<void>;

  componentWillLoad() {
    this.applyScheme();
    this.handleIconColor();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.initProgressText();
    this.handleIconColor();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
    this.handleIconColor();
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }
  @Watch("iconColor")
  handleIconColor() {
    // this._iconColor = !!this.iconColor ? this.iconColor : `var(--${this.activeScheme.iconColor})`;
    !!this.iconColor && setCssVariable(this.hostElement, "--iconColor", `var(--eon-${this.iconColor}`);
  }

  /**
   * Sets the progress text to the current provided progress
   */
  initProgressText() {
    if (this.animateText) {
      this.animateProgress(this.progress, 0); //initially count up the value from zero
    } else {
      this.progressText = this.progress.toString();
    }
  }

  @Watch("progress")
  animateProgress(newValue: number, oldValue: number) {
    if (this.animateText) {
      this.animateProgressText(oldValue, newValue, 500);
    } else {
      this.progressText = newValue.toString();
    }
  }

  /**
   * Counts from given start to end an changes the progressText on each step so the text is shown animated
   * @param start start of the number Animation
   * @param end end of the number Animation
   * @param duration animation duration
   */
  animateProgressText(start: number, end: number, duration) {
    if (start === end || !start || !end) return;
    const range = end - start;
    let current = start;
    const increment = end > start ? 1 : -1;
    const stepTime = Math.abs(Math.floor(duration / range));

    var timer = setInterval(() => {
      current += increment;
      this.progressText = current.toString();
      if (current == end) {
        clearInterval(timer);
      }
    }, stepTime);
  }

  /**
   * Event fires when the icon is clicked
   */
  handleIconClick() {
    this.iconClick.emit();
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({ "progress-circle": true, "progress-circle--icon": !!this.iconName && this.progress < 100 })}
          data-progress={this.progress}
        >
          <div
            class={pfx({
              "circle-center-wrapper": true
            })}
          >
            {this.iconName && (
              <eon-ui-icon
                name={this.iconName}
                size="normal"
                class={pfx({ "circle-center-wrapper__icon": true })}
                onClick={() => this.handleIconClick()}
                // style={{ color: this._iconColor }}
              ></eon-ui-icon>
            )}
            {this.showProgressText && (
              <eon-ui-text
                class={pfx({ "circle-center-wrapper__text": true })}
                text-style="copy-small"
              >{`${this.progressText}%`}</eon-ui-text>
            )}
          </div>

          <div class={pfx({ circle: true })}>
            <div class={pfx({ "progress-circle__slice": true, full: true })}>
              <div class={pfx({ "progress-circle__fill": true })}></div>
            </div>
            <div class={pfx({ "progress-circle__slice": true })}>
              <div class={pfx({ "progress-circle__fill": true, "progress-circle__fill--error": this.error })}></div>
              <div
                class={pfx({
                  "progress-circle__fill": true,
                  "progress-circle__fill--error": this.error,
                  "progress-circle__bar": true
                })}
              ></div>
            </div>
          </div>
          <div class={pfx({ "progress-circle__overlay": true })}></div>
        </div>
      </Host>
    );
  }
}
